import { useApolloClient } from '@apollo/client';
import { Space, Button, message } from 'antd';
import { saveAs } from 'file-saver';
import { Formik } from 'formik';
import { getOr } from 'lodash/fp';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import asyncMap from '../../../../server/utils/asyncMap';
import { type RoleSpecsFragment } from '../../../api/fragments/RoleSpecs';
import {
    AttachPermissionOnRoleDocument,
    type AttachPermissionOnRoleMutation,
    type AttachPermissionOnRoleMutationVariables,
} from '../../../api/mutations/attachPermissionOnRole';
import { useListAvailablePermissionsQuery } from '../../../api/queries/listAvailablePermissions';
import DrawerWithAutoWidth from '../../../components/DrawerWithAutoWidth';
import useHandleError from '../../../utilities/useHandleError';
import useValidator from '../../../utilities/useValidator';
import validators from '../../../utilities/validators';
import RoleAddPermissionForm from './RoleAddPermissionForm';

export type RoleAddPermissionDrawerProps = {
    role: RoleSpecsFragment;
    onClose: () => void;
    visible: boolean;
};

export type FormValues = { permissions: string[] };

const initialValues: FormValues = { permissions: [] };

const formValidator = validators.compose(validators.requiredArray('permissions'));

const RoleAddPermissionDrawer = ({ role, onClose, visible }: RoleAddPermissionDrawerProps) => {
    const { t } = useTranslation(['roleDetails']);

    const [isFormValid, setIsFormValid] = useState<boolean>(false);
    const apolloClient = useApolloClient();
    const roleId = role.id;

    const validate = useValidator(formValidator);

    const onSubmit = useHandleError(
        async (values: FormValues) => {
            message.loading({
                content: t('roleDetails:permissionDrawer.messages.submitting'),
                key: 'primary',
                duration: 0,
            });

            await apolloClient
                .mutate<AttachPermissionOnRoleMutation, AttachPermissionOnRoleMutationVariables>({
                    mutation: AttachPermissionOnRoleDocument,
                    variables: { roleId, permissionIds: values.permissions },
                })
                .finally(() => {
                    message.destroy('primary');
                });

            message.success({
                content: t('roleDetails:permissionDrawer.messages.success'),
                key: 'primary',
            });

            // then close the drawer
            onClose();
        },
        [onClose, t, apolloClient, roleId]
    );

    const { data, loading } = useListAvailablePermissionsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { roleId: role.id },
        skip: !visible,
    });

    const permissions = useMemo(() => data?.permissions || [], [data]);

    const [isDownloading, setIsDownloading] = useState(false);
    const download = useCallback(async () => {
        setIsDownloading(true);
        const data = JSON.stringify(
            await asyncMap(permissions, permission => ({
                name: permission.displayName,
                description: permission.description,
                policies: getOr([], 'raw.policies', permission),
            })),
            null,
            2
        );
        saveAs(new Blob([data], { type: 'application/json;charset=utf-8' }), 'permissions.json');
        setIsDownloading(false);
    }, [permissions, setIsDownloading]);

    return (
        <DrawerWithAutoWidth
            extra={
                <Space>
                    <Button onClick={onClose}>{t('roleDetails:permissionDrawer.cancelButton')}</Button>
                    <Button loading={isDownloading} onClick={download}>
                        {t('roleDetails:permissionDrawer.downloadButton')}
                    </Button>
                    <Button disabled={!isFormValid} form="addPermissions" htmlType="submit" type="primary">
                        {t('roleDetails:permissionDrawer.submitButton')}
                    </Button>
                </Space>
            }
            onClose={onClose}
            title={t('roleDetails:permissionDrawer.title')}
            visible={visible}
            destroyOnClose
        >
            <Formik initialValues={initialValues} onSubmit={onSubmit} validate={validate} validateOnMount>
                {({ handleSubmit }) => (
                    <RoleAddPermissionForm
                        loading={loading}
                        onSubmit={handleSubmit}
                        permissions={permissions}
                        role={role}
                        setIsFormValid={setIsFormValid}
                    />
                )}
            </Formik>
        </DrawerWithAutoWidth>
    );
};

export default RoleAddPermissionDrawer;

export const useAddPermissionDrawer = (role: RoleAddPermissionDrawerProps['role']) => {
    const [visible, setVisible] = useState(false);

    const actions = useMemo(
        () => ({
            open: () => setVisible(true),
            close: () => setVisible(false),
        }),
        [setVisible]
    );

    return {
        ...actions,
        render: () => <RoleAddPermissionDrawer onClose={actions.close} role={role} visible={visible} />,
    };
};
